import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { Store } from '@ngrx/store';
import { forEach as _forEach, get as _get, has as _has } from 'lodash';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, take } from 'rxjs/operators';

import {
  PaymentMethodActions,
  PaymentMethodFacade,
  PaymentMethodSelectors
} from '@amfam/billing/payment-method/data-access';
import { PaymentMethodFeatureActions } from '@amfam/billing/payment-method/feature';
import {
  PaymentMethod,
  PaymentMethodAccountType,
  PaymentMethodAddEditConfig,
  PaymentMethodAddEditOperationType,
  PaymentMethodRow,
  PaymentMethodUtilService
} from '@amfam/billing/payment-method/util';
import { AnalyticsFacade } from '@amfam/shared/analytics';
import { BillingPaymentPaths } from '@amfam/shared/models';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { UtilService } from '@amfam/shared/utility/shared-services';
import { DockingBarService, DsModalService, LoadingSpinnerService } from '@amfam/ui-kit';

@Component({
  selector: 'ds-payment-methods',
  templateUrl: './payment-methods.component.html',
  styleUrls: ['./payment-methods.component.scss'],
  standalone: false
})
export class PaymentMethodsComponent implements OnInit, OnDestroy {
  @Input() tableOnly = false;
  @Input() displaySavedPayments = false;
  @Output() editPaymentMethodEvent: EventEmitter<PaymentMethodAddEditConfig> =
    new EventEmitter<PaymentMethodAddEditConfig>();

  @ViewChild('headerCellTpl', { static: true }) headerCellTpl: TemplateRef<unknown>;
  @ViewChild('removeCellTpl') removeCellTpl: TemplateRef<unknown>;
  @ViewChild('editCellTpl', { static: true }) editCellTpl: TemplateRef<unknown>;
  @ViewChild('methodDetailsCellTpl', { static: true }) methodDetailsCellTpl: TemplateRef<unknown>;
  @ViewChild('accountDetailsCellTpl', { static: true }) accountDetailsCellTpl: TemplateRef<unknown>;
  @ViewChild('paymentMethodsTable') table;

  // Table variables
  rows: PaymentMethodRow[] = [];
  columns = [];
  /* eslint-disable quotes, @typescript-eslint/quotes */
  rowHeight = "'auto'";
  detailRowHeight = "'auto'";
  cssClasses = 'material noshadow expandable payment-method';
  pageLimit = 5;
  footerHeight = '';
  newBillingDesign = false;
  savedPaymentMethods = [];

  // Localized spinner
  dataLoading: Observable<boolean>;
  paymentMethodAddEditConfig$: BehaviorSubject<PaymentMethodAddEditConfig> = new BehaviorSubject(
    null
  );
  // Shared subject for completing observables
  private stop$: Subject<void> = new Subject<void>();

  constructor(
    private store: Store,
    private feature: FeatureFlagService,
    private paymentMethodUtil: PaymentMethodUtilService,
    private modalService: DsModalService,
    private utilService: UtilService,
    private spinner: LoadingSpinnerService,
    private paymentMethodFacade: PaymentMethodFacade,
    private analyticsFacade: AnalyticsFacade,
    private dockingBarService: DockingBarService
  ) {}

  ngOnInit() {
    this.newBillingDesign = this.feature.isEnabled('new-billing-design');
    if (this.newBillingDesign) {
      this.dockingBarService.hide();
    }
    this.dataLoading = this.store.select(PaymentMethodSelectors.selectIsDataLoading);
    this.buildPaymentMethodTable();

    this.store
      .select(PaymentMethodSelectors.selectCombinedPaymentData)
      .pipe(filter(state => !state.loading))
      .subscribe(state => {
        const filteredPaymentMethods = state?.paymentAccounts?.filter(function (x) {
          return x !== undefined;
        });
        const paymentMethodList: PaymentMethod[] = filteredPaymentMethods;
        if (this.newBillingDesign) {
          this.setSavedPaymentMethods(paymentMethodList);
        }
        if (state.newPaymentAccountId) {
          const fromIndex = paymentMethodList.findIndex(
            account => account?.paymentAccountId === state.newPaymentAccountId
          );
          if (fromIndex > 0) {
            const element = paymentMethodList.splice(fromIndex, 1)[0];
            paymentMethodList.splice(0, 0, element);
          }
        }
        this.rows = [];
        _forEach(paymentMethodList, (paymentAccount: PaymentMethod) => {
          this.rows.push({
            accType: _has(paymentAccount, 'creditCard') ? 'Card' : 'Bank',
            paymentMethod: paymentAccount,
            paymentMethodDetails: _has(paymentAccount, 'creditCard')
              ? paymentAccount?.creditCard
              : paymentAccount?.achWithdrawal
          });
        });

        // Enable pagination if we have enough methods
        if (this.rows.length > this.pageLimit) {
          this.footerHeight = 'auto';
        } else {
          this.footerHeight = '';
        }
      });
  }

  onEditEvent(row) {
    let config: PaymentMethodAddEditConfig;
    if (this.newBillingDesign) {
      config = {
        accountType: this.paymentMethodUtil.getPaymentMethodAccountType(row),
        operation: PaymentMethodAddEditOperationType.EDIT,
        paymentMethod: row
      };
    } else {
      config = {
        accountType: this.paymentMethodUtil.getPaymentMethodAccountType(row.paymentMethod),
        operation: PaymentMethodAddEditOperationType.EDIT,
        paymentMethod: row.paymentMethod
      };
    }
    // analytics for payment method stand alone experience
    const buttonAnalyticName =
      config.accountType === PaymentMethodAccountType.CHECKING
        ? 'Checking:EditPaymentMethod'
        : config.accountType === PaymentMethodAccountType.SAVINGS
          ? 'Savings:EditPaymentMethod'
          : 'DebitCredit:EditPaymentMethod';
    this.paymentMethodFacade.sendPaymentMethodAnalytics({ buttonAnalyticName });
    this.paymentMethodAddEditEvent(config);
    this.analyticsFacade.trackEvent({
      event: 'edit_pay_method',
      payment_type:
        config.accountType === PaymentMethodAccountType.CHECKING ||
        config.accountType === PaymentMethodAccountType.SAVINGS
          ? 'Checking/Savings'
          : 'Credit/Debit'
    });
  }

  onDeleteEvent(row: PaymentMethodRow) {
    // analytics for payment method stand alone experience
    const accountType = this.paymentMethodUtil.getPaymentMethodAccountType(row.paymentMethod);
    const buttonAnalyticName =
      accountType === PaymentMethodAccountType.CHECKING
        ? 'Checking:DeletePaymentMethod'
        : accountType === PaymentMethodAccountType.SAVINGS
          ? 'Savings:DeletePaymentMethod'
          : 'DebitCredit:DeletePaymentMethod';
    this.paymentMethodFacade.sendPaymentMethodAnalytics({ buttonAnalyticName });

    this.store.dispatch(
      PaymentMethodActions.storePaymentAccountId({
        paymentAccountId: row.paymentMethod.paymentAccountId
      })
    );
    this.store.dispatch(
      fromRouterActions.Go({
        path: [BillingPaymentPaths.DELETE_PAYMENT_METHOD]
      })
    );
  }

  onPageChanged() {
    if (_get(this.table, 'rowDetail')) {
      this.table.rowDetail.collapseAllRows();
    }
  }

  paymentMethodAddEditEvent(config: PaymentMethodAddEditConfig) {
    this.paymentMethodAddEditConfig$.next(config);
    this.modalService.open('paymentMethodModal');
    let pageAnalyticName;
    if (config.operation === PaymentMethodAddEditOperationType.ADD) {
      if (config.accountType === PaymentMethodAccountType.CREDIT_DEBIT) {
        pageAnalyticName = 'AddPaymentMethod:SetupCreditDebitCard';
      } else {
        pageAnalyticName = 'AddPaymentMethod:SetupSavingsChecking';
      }
    } else {
      if (config.accountType === PaymentMethodAccountType.CREDIT_DEBIT) {
        pageAnalyticName = 'EditPaymentMethod:EditCard';
      } else {
        pageAnalyticName = 'EditPaymentMethod:EditCheckingSavings';
      }
    }
    setTimeout(() => {
      this.paymentMethodFacade.sendPaymentMethodAnalytics({ pageAnalyticName });
    }, 0);
  }

  paymentMethodAddEditComplete(): void {
    this.modalService.close('paymentMethodModal');

    const correlationId: string = this.utilService.generateId();
    this.spinner.start({
      blockActions: true
    });
    this.store.dispatch(PaymentMethodActions.getPaymentMethods({ correlationId }));
    this.store
      .select(PaymentMethodSelectors.selectPaymentMethodState)
      .pipe(
        filter(state => state.correlationId === correlationId),
        take(1)
      )
      .subscribe(state => {
        this.spinner.stop();

        if (!state.hasError) {
          const operation: 'add' | 'edit' | 'delete-success' | 'delete-error' =
            this.paymentMethodAddEditConfig$.getValue().operation;
          this.store.dispatch(PaymentMethodFeatureActions.setPaymentMethodOperation({ operation }));
          //route
          this.store.dispatch(
            fromRouterActions.Go({
              path: [BillingPaymentPaths.CONFIRMATION_PAYMENT_METHOD]
            })
          );
        }
      });
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
  }
  setSavedPaymentMethods(paymentMethodList) {
    this.savedPaymentMethods = [];
    _forEach(paymentMethodList, (paymentAccount: PaymentMethod) => {
      this.savedPaymentMethods.push({
        paymentMethod: paymentAccount.nickName,
        methodDesc: this.setMethodDesc(paymentAccount),
        paymentAccount: paymentAccount
      });
    });
  }

  setMethodDesc(paymentAccount) {
    let accountData;
    if (_has(paymentAccount, 'creditCard')) {
      const cardDetails = paymentAccount?.creditCard;
      accountData = cardDetails.cardType + ' •••• ' + cardDetails.cardNumber.slice(-4);
    } else {
      const bankDetails = paymentAccount?.achWithdrawal;
      accountData = bankDetails.accountType + ' •••• ' + bankDetails?.accountNumber.slice(-3);
    }
    return accountData;
  }

  addPaymentMethodModal() {
    this.modalService.open('addPaymentMethodModalId');
  }

  private buildPaymentMethodTable() {
    this.columns = [
      {
        cellTemplate: this.methodDetailsCellTpl,
        headerTemplate: this.headerCellTpl,
        isHeaderOnMobile: true,
        name: 'Method',
        prop: 'paymentMethodDetails',
        draggable: false,
        resizeable: false,
        canAutoResize: true,
        width: 270
      },
      {
        cellTemplate: this.editCellTpl,
        headerTemplate: this.headerCellTpl,
        name: ' ',
        prop: 'paymentMethod',
        draggable: false,
        resizeable: false,
        canAutoResize: false,
        width: 180
      }
    ];
  }
}
